import Button from 'components/Button';
import Price, {
	BasePriceText,
	hasReducedPrice,
	PriceText,
} from 'components/Price';
import Text from 'components/Text';
import {
	useGlobalMemberPopoversContext,
	usePriceContext,
	useUserContext,
} from 'contexts';
import { useDateFormatter } from 'hooks/format';
import type { ProductPrice } from 'models/price';
import { cn } from 'utils/classNames';
import { formatTemplateToNodes } from 'utils/format';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	basePrice: ProductPrice | undefined;
	displayPrice: ProductPrice | undefined;
}

export default function Prices({ basePrice, displayPrice }: Props) {
	const { t } = useI18n();
	const formatDate = useDateFormatter();

	const { usePriceExcVat } = usePriceContext();
	const { openLogin } = useGlobalMemberPopoversContext();
	const userInfo = useUserContext();

	if (!displayPrice) {
		return null;
	}
	const { priceType } = displayPrice;
	const isReducedPrice = hasReducedPrice(priceType);
	const vatPriceText = usePriceExcVat
		? displayPrice.priceIncVat
			? formatTemplateToNodes(t('product_details_price_vat_included_text'), {
					priceInclVat: <PriceText price={displayPrice.priceIncVat} />,
				})
			: undefined
		: displayPrice.priceExcVat
			? formatTemplateToNodes(t('product_details_price_vat_excluded_text'), {
					priceExclVat: <PriceText price={displayPrice.priceExcVat} />,
				})
			: undefined;

	return (
		<>
			<div>
				<Price price={displayPrice} size="xl" />

				{vatPriceText && (
					<Text
						as="pSmall"
						className={cn(
							isReducedPrice && 'text-reducedPrice',
							priceType !== 'Base' && 'mt-2',
						)}
					>
						{vatPriceText}
					</Text>
				)}
			</div>

			<div className="mt-6 empty:hidden">
				{is.positiveNumber(displayPrice.maxPurchaseQuantity) &&
					isReducedPrice && (
						<Text as="pSmall" className="mb-2 font-bold text-reducedPrice">
							{t('product_details_maximum_purchase_quantity_text', {
								amount: displayPrice.maxPurchaseQuantity,
							})}
						</Text>
					)}

				{displayPrice.comparisonPrice?.title && isReducedPrice && (
					<PriceText
						as="p"
						price={displayPrice.comparisonPrice}
						prefix={displayPrice.comparisonPrice.title}
						isReducedPrice
						className="text-sm"
					/>
				)}

				{priceType !== 'Base' && basePrice && (
					<BasePriceText
						basePrice={basePrice}
						displayedPriceType={priceType}
						recentLowestPriceKey="product_details_recent_lowest_price_text"
						basePriceKey="product_details_base_price_text"
						className="text-sm font-bold"
					/>
				)}

				{basePrice?.comparisonPrice?.title && (
					<PriceText
						as="p"
						price={basePrice.comparisonPrice}
						prefix={basePrice.comparisonPrice.title}
						className="text-sm"
					/>
				)}

				{displayPrice.validTo && (
					<Text as="pSmall">
						{t(
							priceType === 'JulaClub'
								? 'product_details_jula_club_valid_to_text'
								: 'product_details_valid_to_text',
							{ validTo: formatDate(displayPrice.validTo) },
						)}
					</Text>
				)}

				{userInfo?.customerType === 'Anonymous' &&
					is.oneOf(priceType, 'JulaClub', 'JulaPro') && (
						<Button size="sm" variant="text" onClick={() => openLogin()}>
							{priceType === 'JulaPro'
								? t('product_details_jula_pro_member_nudge_text')
								: t('product_details_jula_club_member_nudge_text')}
						</Button>
					)}
			</div>
		</>
	);
}
Prices.displayName = 'Prices';
